// Port of ios/Sources/ViewModels/ForwardPresentation.swift.
//
// Forwarding a message to another conversation — the desktop's Forward dialog, at mobile
// proportions (shared/API.md §31).
//
// There is no forward ENDPOINT: on the desktop, forwarding posts a new message whose text is a
// permalink to the original, with the forwarder's optional comment above it. The server then
// renders that permalink as an embedded preview of the message being pointed at. This client
// posts exactly the same text, so a message forwarded from a phone and one forwarded from the
// desktop are the same post — anything cleverer would be a private convention only this app
// could read back.
import type { ChannelRecord } from './records';
import { fold } from './searchQuery';

/**
 * `teamName` is the team a channel belongs to, or null when it does not need saying — a DM
 * belongs to no team, and repeating the team the reader is already in on every row is noise
 * on the rows they will pick from most.
 */
export interface Target {
  channelId: string;
  name: string;
  teamName: string | null;
  /** The wire's channel type: `O` public, `P` private, `D` direct, `G` group. */
  type: string;
  lastPostAt: number;
}

export function isDirect(target: Pick<Target, 'type'>): boolean {
  return target.type === 'D' || target.type === 'G';
}

export function isPrivate(target: Pick<Target, 'type'>): boolean {
  return target.type === 'P';
}

/**
 * Every conversation the reader can forward INTO, most recently active first.
 *
 * Across every team, deliberately, matching the desktop: the message worth forwarding is
 * regularly one somebody in another team needs, and a picker scoped to the open workspace
 * would silently make that impossible rather than merely awkward. A channel id is global,
 * so the post lands correctly wherever it is sent.
 *
 * Ordered by last activity rather than alphabetically. A forward goes to a conversation
 * the reader is already in the middle of far more often than to one they have to hunt for.
 * A conversation that resolves to no name is DROPPED rather than shown blank: a row nobody
 * can read is a row nobody can choose deliberately.
 */
export function targets(
  channels: readonly ChannelRecord[],
  names: Readonly<Record<string, string>>,
  teamNames: Readonly<Record<string, string>>,
  currentTeamId: string,
): Target[] {
  const out: Target[] = [];
  for (const channel of channels) {
    if (channel.deleteAt !== 0) continue;
    const raw = names[channel.id];
    if (raw == null || raw.trim() === '') continue;
    let teamName: string | null;
    if (channel.type === 'D' || channel.type === 'G') {
      teamName = null;
    } else if (channel.teamId === '' || channel.teamId === currentTeamId) {
      teamName = null;
    } else {
      teamName = teamNames[channel.teamId] ?? null;
    }
    out.push({ channelId: channel.id, name: raw, teamName, type: channel.type, lastPostAt: channel.lastPostAt });
  }
  return out.sort((a, b) => {
    if (a.lastPostAt !== b.lastPostAt) return b.lastPostAt - a.lastPostAt;
    // A tie is every conversation nobody has written in yet; alphabetical keeps
    // their order stable between openings instead of leaving it to row order.
    const ka = sortKey(a.name);
    const kb = sortKey(b.name);
    if (ka !== kb) return ka < kb ? -1 : 1;
    return a.channelId < b.channelId ? -1 : a.channelId > b.channelId ? 1 : 0;
  });
}

/**
 * Accents folded before comparing, so "Ángeles" sorts with the As. Not decoration on a
 * Spanish deployment: a plain code-unit comparison files every accented letter ABOVE `z`.
 * (Swift: `folding(options: [.diacriticInsensitive, .caseInsensitive])` — NFD minus marks,
 * lowercased.)
 */
function sortKey(name: string): string {
  return name.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/**
 * The picker's filter, applied to a list already read from the store — never a re-read.
 * Matches the SLUG as well as the display name because that is what people type for a
 * channel they know well ("anuncios-rh").
 */
export function filtered(
  list: readonly Target[],
  filter: string,
  slugs: Readonly<Record<string, string>>,
): Target[] {
  const needle = fold(filter.replace(/^[\p{Zs}\t]+|[\p{Zs}\t]+$/gu, ''));
  if (needle === '') return [...list];
  return list.filter(
    (target) => fold(target.name).includes(needle) || fold(slugs[target.channelId] ?? '').includes(needle),
  );
}

/**
 * The text that actually gets posted: the comment, then the permalink on its own line.
 * One newline, not two — the webapp's own shape. A blank comment posts the bare permalink,
 * which must not carry a leading empty line.
 */
export function message(permalink: string, comment: string): string {
  const trimmed = comment.trim();
  return trimmed === '' ? permalink : trimmed + '\n' + permalink;
}
